// VirtualizedPageItem.tsx - Enhanced with Safari Memory Management
import { FC } from 'react'
import { ProgressivePage } from './ProgressivePage'
import type { CSSProperties } from 'react'
import './Core.css'
import type { EditableFieldsConfig } from '@/components/EditableFields/EditableFieldsOverlay'

export const VirtualizedPageItem: FC<{
  index: number
  style: CSSProperties
  data: {
    numPages: number
    visiblePages: Set<number>
    pageWidth: number
    pageScale: number
    pageAspectRatio: number
    onMemoryChange: (
      pageNumber: number,
      width: number,
      height: number,
      pixelRatio: number,
      action: 'add' | 'remove'
    ) => void
    isSafari?: boolean
    registerRenderTask?: (pageNumber: number, renderTask: unknown) => void
    unregisterRenderTask?: (pageNumber: number) => void
    editableFields?: EditableFieldsConfig
  }
}> = ({ index, style, data }) => {
  const pageNumber = index + 1
  const {
    visiblePages,
    pageWidth,
    pageScale,
    pageAspectRatio,
    onMemoryChange,
    isSafari,
    registerRenderTask,
    unregisterRenderTask,
    editableFields,
  } = data
  // Calculate height to match itemHeight calculation in Renderer.tsx
  // This ensures FixedSizeList scrolling matches actual content height
  const height = pageWidth * pageAspectRatio * pageScale + 24

  return (
    <div style={style}>
      {visiblePages.has(pageNumber) ? (
        <ProgressivePage
          pageNumber={pageNumber}
          width={pageWidth}
          pageAspectRatio={pageAspectRatio}
          onMemoryChange={onMemoryChange}
          registerRenderTask={registerRenderTask}
          unregisterRenderTask={unregisterRenderTask}
          editableFields={editableFields}
        />
      ) : (
        <div
          className="pdf-core__page-container"
          style={
            {
              '--pc-min-height': `${height}px`,
              '--pc-justify': pageScale > 1 ? 'flex-start' : 'center',
              '--pc-pointer-events': pageScale > 1 ? 'auto' : 'none',
            } as CSSProperties
          }
        >
          <div
            className="pdf-core__placeholder-page"
            style={
              {
                '--pp-width': `${pageWidth}px`,
                '--pp-height': `${pageWidth * pageAspectRatio}px`,
              } as CSSProperties
            }
          >
            <div className="pdf-core__placeholder-text">Page {pageNumber}</div>
            <div className="pdf-core__placeholder-subtext">
              {isSafari ? 'Safari optimized - scroll to load' : 'Scroll to load'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
